/* CSV-verktøy for prisliste: tolking (semikolon/komma/tab, hermeteikn), kolonnemapping og eksport. */

const strip = (s) => (s || "").replace(/^\uFEFF/, "");

/** Finn skiljeteikn ut frå første linje: semikolon (norsk Excel), tab eller komma. */
export function detectDelimiter(text) {
  const first = strip(text).split(/\r?\n/).find((l) => l.trim()) || "";
  const counts = [";", "\t", ","].map((d) => [d, first.split(d).length - 1]);
  counts.sort((a, b) => b[1] - a[1]);
  return counts[0][1] > 0 ? counts[0][0] : ";";
}

/** Tolkar CSV-tekst til rader av strengar. Handterer hermeteikn og linjeskift inni felt. */
export function parseCsv(text, delimiter = detectDelimiter(text)) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  const src = strip(text);
  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === delimiter) {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && src[i + 1] === "\n") i += 1;
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += ch;
    }
  }
  if (field !== "" || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => String(c).trim() !== ""));
}

/** Kolonneoverskrifter vi kjenner att (nynorsk, bokmål, engelsk). */
const HEADER_ALIASES = {
  sku: ["varenr", "varenummer", "varenr.", "sku", "produktnr", "produktnummer", "artikkelnr", "artikkelnummer", "nr", "produktkode", "kode", "id"],
  name: ["namn", "navn", "name", "produkt", "produktnamn", "produktnavn", "vare", "varenamn", "varenavn", "teneste", "tjeneste", "tittel"],
  category: ["kategori", "category", "type", "gruppe", "varegruppe"],
  unit: ["eining", "enhet", "unit", "einingar", "enheter", "måleeining", "måleenhet"],
  unit_price: ["pris", "einingspris", "enhetspris", "pris eks. mva", "pris eks mva", "pris ekskl. mva", "pris ekskl mva", "price", "unit_price", "unitprice", "sats", "nettopris", "salgspris", "salspris", "utpris", "pris (kr)"],
  vat_rate: ["mva", "mva-sats", "mva sats", "mvasats", "mva %", "mva%", "vat", "vat_rate", "moms", "avgift"],
  density_t_per_m3: ["densitet", "density", "t/m3", "t/m³", "tettleik", "tetthet", "vekt per m3", "vekt per m³"],
  description: ["skildring", "beskrivelse", "description", "merknad", "kommentar", "notat", "info"],
  active: ["aktiv", "active", "status"],
};

export const IMPORT_COLUMNS = [
  { key: "sku", label: "Varenr" },
  { key: "name", label: "Namn", required: true },
  { key: "category", label: "Kategori" },
  { key: "unit", label: "Eining" },
  { key: "unit_price", label: "Pris eks. mva" },
  { key: "vat_rate", label: "MVA %" },
  { key: "density_t_per_m3", label: "Densitet (t/m³)" },
  { key: "description", label: "Skildring" },
  { key: "active", label: "Aktiv" },
];

const normHeader = (h) => String(h || "").trim().toLowerCase().replace(/\s+/g, " ").replace(/[*:]/g, "");

/** Kartlegg overskriftsrad -> feltnøklar. Returnerer null om vi ikkje finn att namn-kolonna. */
export function mapHeader(headerRow) {
  const mapped = headerRow.map((h) => {
    const key = normHeader(h);
    const hit = Object.entries(HEADER_ALIASES).find(([, aliases]) => aliases.includes(key));
    return hit ? hit[0] : null;
  });
  // Vi godtek òg at første ukjende tekstkolonne er namn om det manglar
  if (!mapped.includes("name") && !mapped.includes("sku")) return null;
  return mapped;
}

/** Ser ei rad ut som overskrifter (ingen tal i pris-liknande posisjonar, treff på kjende namn)? */
export function looksLikeHeader(row) {
  const mapped = row.map((h) => normHeader(h)).filter(Boolean);
  const hits = mapped.filter((key) => Object.values(HEADER_ALIASES).some((aliases) => aliases.includes(key)));
  return hits.length >= Math.max(1, Math.ceil(mapped.length / 3));
}

/**
 * Bygg import-rader frå CSV-tekst.
 * Returnerer { rows, columns, delimiter, header, skipped } der rows er klare for POST /catalog/import.
 */
export function buildImportRows(text) {
  const delimiter = detectDelimiter(text);
  const table = parseCsv(text, delimiter);
  if (!table.length) return { rows: [], columns: [], delimiter, header: false, error: "Fann ingen rader i fila." };
  let columns;
  let dataRows;
  if (looksLikeHeader(table[0])) {
    columns = mapHeader(table[0]);
    dataRows = table.slice(1);
    if (!columns) return { rows: [], columns: [], delimiter, header: true, error: "Fann ikkje kolonna «Namn» eller «Varenr» i overskriftsrada." };
  } else {
    // Utan overskrift: bruk standardrekkjefølgja frå malen
    columns = IMPORT_COLUMNS.map((c) => c.key).slice(0, table[0].length);
    dataRows = table;
  }
  const rows = dataRows.map((cells) => {
    const obj = {};
    columns.forEach((key, i) => {
      if (!key) return;
      const value = String(cells[i] ?? "").trim();
      if (value !== "") obj[key] = value;
    });
    return obj;
  });
  return { rows, columns, delimiter, header: dataRows !== table, error: null };
}

const escapeCell = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  // Hindre formel-injeksjon i rekneark og pakk inn ved skiljeteikn/linjeskift
  const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return /[";\n\r,\t]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

/** Lag CSV (semikolon, norsk desimalkomma, UTF-8 BOM for Excel). */
export function toCsv(headers, rows) {
  const lines = [headers.map(escapeCell).join(";"), ...rows.map((r) => r.map(escapeCell).join(";"))];
  return `\uFEFF${lines.join("\r\n")}\r\n`;
}

export const decimalNb = (v) => (v === null || v === undefined || v === "" ? "" : String(v).replace(".", ","));

export function downloadText(filename, text, type = "text/csv;charset=utf-8") {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
